// packages/web-ui/src/hooks/useOpenResource.ts
//
// W2-S4 AC #11 — single canonical entry point for opening a tab from any UI
// panel (chat-card click, KB tree click, file-tree click, View thumbnail,
// changeset CTA, etc.). The cross-panel link map (UX §2.4) is enforced by
// every panel calling this hook — no panel calls `navigation.openTab(...)`
// directly (Pitfall #11).

import { useCallback } from 'react';
import { useRootStore } from '../state/store.js';
import type { ActiveDestination } from '../state/navigation.js';

export type OpenResourceFn = (
  destination: ActiveDestination,
  resourceId: string | null,
  opts?: { activate?: boolean; label?: string },
) => void;

/**
 * Returns a stable callback that opens (or activates) the tab matching
 * `{destination, resourceId}`. The label is derived from `getDefaultTabLabel`
 * unless `opts.label` is provided.
 */
export function useOpenResource(): OpenResourceFn {
  return useCallback((destination, resourceId, opts) => {
    const label = opts?.label ?? getDefaultTabLabel(destination, resourceId);
    useRootStore.getState().openTab({
      destination,
      resourceId,
      label,
      activate: opts?.activate ?? true,
    });
  }, []);
}

/**
 * Pure label-derivation helper per AC #11. Exported so callers and tests can
 * reuse it without going through the hook.
 */
export function getDefaultTabLabel(
  destination: ActiveDestination,
  resourceId: string | null,
): string {
  switch (destination) {
    case 'settings':
      return 'Settings';
    case 'chat': {
      if (resourceId === null) return 'Chat';
      const tail = resourceId.slice(-8);
      return `Chat · ${tail}`;
    }
    case 'kb':
      return resourceId ?? 'KB';
    case 'files': {
      if (resourceId === null) return 'Files';
      // Browser-safe basename; works on any host OS regardless of separator
      // because the hash router decodes URI-encoded `/` upstream.
      const parts = resourceId.split('/');
      return parts[parts.length - 1] ?? resourceId;
    }
    case 'views':
      return resourceId === null ? 'View' : `View · ${resourceId}`;
    case 'diff':
      return resourceId === null ? 'Diff' : `Diff · ${resourceId}`;
    case 'git':
      return 'Git';
    case 'skills':
      return resourceId === null ? 'Skills' : `Skill · ${resourceId}`;
  }
}
